<template>
	<b-modal
		id="edit-asset-pool-distribution"
		title="Edit Asset Pool Distribution"
		ref="modal"
		ok-title="Save"
		@ok="handleOk"
		@show="onReset"
		:cancel-disabled="disableConfirmButtons"
		:ok-disabled="disableConfirmButtons"
		:no-close-on-backdrop="true"
	>
		<loading
			:active.sync="isLoading"
			loader="spinner"
			color="#20A8D8"
			:is-full-page="false"
		/>

		<b-form @submit.stop.prevent="handleSubmit" novalidate>
			<b-container fluid>
				<b-row class="my-2">
					<b-col lg="12" md="12" sm="12">
						<b-form-group label="Company" label-class="font-weight-bold pt-0">
							{{ form.company }}
						</b-form-group>
					</b-col>

					<b-col lg="12" md="12" sm="12">
						<b-form-group
							label="Distribution Area"
							label-class="font-weight-bold pt-0"
						>
							{{
								form.connectedCompany + ' - ' + form.connectedStorageLocation
							}}
						</b-form-group>
					</b-col>

					<b-col lg="12" md="12" sm="12">
						<b-form-group
							label="Asset Type"
							label-class="font-weight-bold pt-0"
						>
							{{ form.assetType }}
						</b-form-group>
					</b-col>
					<b-col lg="12" md="12" sm="12">
						<b-form-group
							label="Asset Total Adjustment"
							label-for="Asset Total Adjustment"
							:description="'Current Total: ' + form.total"
							label-class="font-weight-bold pt-0"
						>
							<b-form-input
								id="addTotal"
								name="Asset Total Adjustment"
								type="number"
								v-model="form.addTotal"
								v-validate="{
									required: true,
									regex: /^([0-9-]{1,})$/,
								}"
								placeholder="0"
							/>
							<span
								v-show="errors.has('Asset Total Adjustment')"
								class="help-block"
							>
								{{ errors.first('Asset Total Adjustment') }}
							</span>
						</b-form-group>
						<div class="instruction mb-2">
							You can increase the total count by entering a positive number (i.e. 1). 
							You can decrease the total count by entering a negative number (i.e. -1)
						</div>
						<div
							class="adjusted-total mb-4"
							v-show="form.addTotal && parseInt(form.addTotal) !== 0"
						>
							Adjusted Total: {{ adjustedTotal }}
						</div>
					</b-col>
					<b-col lg="12" md="12" sm="12">
						<b-form-group
							label="Notes"
							label-for="Notes"
							label-class="font-weight-bold pt-0"
						>
							<b-form-textarea
								name="Notes"
								type="text"
								v-model="form.notes"
								maxlength="200"
								:rows="3"
								placeholder="Notes"
								v-validate="{
										required:true,
										regex: /^([0-9:;,.'\\\/()\-_a-zA-Z\n ])*$/
									}"
							/>
							<span v-show="errors.has('Notes')" class="help-block">
								{{ errors.first('Notes') }}
							</span>
						</b-form-group>
					</b-col>
				</b-row>
			</b-container>
		</b-form>
	</b-modal>
</template>

<script>
// Util
import { DateUtil } from '@/utils/dateutil';
import { ValidationUtil } from '@/utils/validationUtil';

// API
import assetPoolDistributionApi from '@/api/assetPoolDistributionApi';

// Others
import EventBus from '@/shared/event-bus';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

export default {
	name: 'edit-asset-pool-distribution',
	components: {
		Loading,
	},
	data() {
		return {
			form: {
				company: '',
				companyId: '',
				connectedCompany: '',
				connectedCompanyId: '',
				connectedStorageLocation: '',
				connectedStorageLocationId: '',
				assetType: '',
				assetTypeId: '',
				total: 0,
				notes: '',
			},

			isSuperAdmin: this.$store.getters.isSuperAdmin,
			currUserId: this.$store.getters.loggedUser.id,

			// Check for loader
			isLoading: false,
		};
	},
	computed: {
		disableConfirmButtons() {
			return this.isLoading;
		},
		adjustedTotal() {
			let total = this.form.total ? this.form.total : 0;
			let adjustment =
				this.form.addTotal && this.form.addTotal !== 0 ? this.form.addTotal : 0;
			let adjustedTotal = parseInt(total) + parseInt(adjustment);

			return adjustedTotal;
		},
	},
	methods: {
		async handleOk(evt) {
			// Prevent modal from closing
			evt.preventDefault();

			// show loading indicator
			this.isLoading = true;

			let isValid = await this.$validator.validateAll();
			if (!isValid) {
				this.$toaster.warning('Please address the field/s with invalid input.');
				// hide loading indicator
				this.isLoading = false;
				return;
			}

			if (this.adjustedTotal < 0) {
				this.$toaster.warning(
					'Invalid Input: Negative adjusted total is not allowed.'
				);
				// hide loading indicator
				this.isLoading = false;
				return;
			}

			if (this.form.addTotal && parseInt(this.form.addTotal) === 0) {
				this.$toaster.warning(
					'Invalid Input: Adjustment value must not be equal to 0.'
				);
				// hide loading indicator
				this.isLoading = false;
				return;
			}

			// Removes excess whitespace
			this.form.notes = ValidationUtil.removeExcessWhiteSpace(this.form.notes);
			this.handleSubmit();
		},
		getParam() {
			return {
				currUserId: this.currUserId,
				currTimeStamp: DateUtil.getCurrentTimestamp(),
				assetPoolDistribution: this.form,
			};
		},
		async handleSubmit() {
			// show loading indicator
			this.isLoading = true;

			try {
				let { data } = await assetPoolDistributionApi.saveAssetPoolDistribution(
					this.getParam()
				);

				// hide loading indicator
				this.isLoading = false;

				if (data.isSuccess) {
					this.$toaster.success(data.message);
					EventBus.$emit(
						'onCloseEditAssetPoolDistribution',
						data.assetPoolDistribution
					);
					this.$refs.modal.hide();
				} else {
					this.$toaster.error(
						`Error updating Asset Pool Distribution "${this.form.company}". Please try again.`
					);
				}
			} catch (error) {
				// hide loading indicator
				this.isLoading = false;

				this.$toaster.error(
					`Error updating Asset Pool Distribution "${this.form.company}". Please try again.`
				);
			}
		},
		onReset() {
			/* Reset our form values */
			let currAPD = this.$store.getters.currAssetPoolDistribution;
			this.form = {
				id: currAPD.id,
				company: currAPD.company,
				companyId: currAPD.companyId,
				connectedCompany: currAPD.connectedCompany,
				connectedCompanyId: currAPD.connectedCompanyId,
				connectedStorageLocation: currAPD.connectedStorageLocation,
				connectedStorageLocationId: currAPD.connectedStorageLocationId,
				assetType: currAPD.assetType,
				assetTypeId: currAPD.assetTypeId,
				assetTypeCategory: currAPD.assetTypeCategory,
				total: currAPD.total,
				notes: '',
				dateCreated: currAPD.dateCreated,
				createdBy: currAPD.createdBy,
				dateUpdated: currAPD.dateUpdated,
				updatedBy: currAPD.updatedBy,
			};

			// reset validation
			this.$validator.reset();
			this.errors.clear();
		},
	},
};
</script>

<style scoped>
.instruction {
	font-size: 10px !important;
	color: #f18f01 !important;
}
.adjusted-total {
	margin-top: 10px;
	font-size: 12px !important;
	color: #122c91 !important;
}
</style>